import { useState } from 'react';
import styled from 'styled-components';
import VizExButton from 'visitor-ui-component-library/button/VizExButton';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { formatHtml } from 'sanitize-text/sanitizers/HtmlSanitizer';
import { NEUTRAL_1600 } from 'visitor-ui-component-library/constants/WidgetColors';
import { ChatWidgetLocaleContextConsumer } from './ChatWidgetLocaleContext';
import { UPDATED_VISITOR_WIDGET_BORDER_RADIUS } from '../widget-dimensions/constants/dimensions';
import VizExSmall from 'visitor-ui-component-library/typography/VizExSmall';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const ConsentModalContainer = styled.div.withConfig({
  displayName: "ConsentModal__ConsentModalContainer"
})(["position:absolute;height:100%;display:flex;align-items:flex-start;justify-content:center;width:100%;background-color:rgba(45,62,80,0.79);backdrop-filter:blur(2px);padding-top:20px;border-bottom-left-radius:", "px;border-bottom-right-radius:", "px;overflow-y:hidden;"], UPDATED_VISITOR_WIDGET_BORDER_RADIUS, UPDATED_VISITOR_WIDGET_BORDER_RADIUS);
const ContentContainer = styled.div.withConfig({
  displayName: "ConsentModal__ContentContainer"
})(["display:flex;align-items:center;justify-content:space-between;flex-wrap:wrap;padding:20px;background-color:white;border-radius:3px;box-shadow:0 2px 4px -1px rgba(0,0,0,0.75);margin:0 20px;"]);
const SmallNoParagraphPadding = styled(VizExSmall).withConfig({
  displayName: "ConsentModal__SmallNoParagraphPadding"
})(["display:block;max-height:272px;word-break:break-word;word-wrap:break-word;-ms-word-break:break-all;p{margin-bottom:0;}", " overflow-y:auto;"], ({
  $useV2Style
}) => $useV2Style ? `
    font-weight: 300;
    font-size: 12px;
    line-height: 21px;
    color: ${NEUTRAL_1600};
  ` : '');
const LegacyFullWidthButton = styled(VizExButton).withConfig({
  displayName: "ConsentModal__LegacyFullWidthButton"
})(["padding:9px 16px;margin:0 auto;width:fit-content;flex:0 0 auto;border-radius:999px;&:not(:first-child){margin-left:8px;}"]);
const AIConsentButton = styled(VizExButton).withConfig({
  displayName: "ConsentModal__AIConsentButton"
})(["font-weight:300;height:32px;min-height:32px;padding:0 16px;font-size:13px;line-height:18px;&:not(:first-child){margin-left:8px;}"]);
const ButtonsContainer = styled.div.withConfig({
  displayName: "ConsentModal__ButtonsContainer"
})(["display:flex;flex-direction:row;margin:12px 0 0 0;width:100%;"]);
const Header = styled.p.withConfig({
  displayName: "ConsentModal__Header"
})(["text-transform:none;font-size:18px;font-weight:500;margin-top:18px;margin-bottom:12px;line-height:normal;"]);
const ConsentModal = ({
  headerMessage,
  message,
  onClick = () => {},
  onDeclineClick = () => {},
  useV2Style = false
}) => {
  const [state, setState] = useState({
    isEntering: true,
    shouldRender: true,
    callback: () => {}
  });
  const onAnimationEnd = () => {
    if (!state.isEntering) {
      state.callback();
      setState(Object.assign({}, state, {
        shouldRender: false
      }));
    }
  };
  const onClose = cb => {
    setState(Object.assign({}, state, {
      isEntering: false,
      callback: cb
    }));
  };
  if (!state.shouldRender) {
    return null;
  }
  return /*#__PURE__*/_jsx(ConsentModalContainer, {
    style: {
      animation: `${state.isEntering ? 'consent-modal-background-fadeIn' : 'consent-modal-background-fadeOut'} .3s`
    },
    "data-test-id": "gdpr-consent-prompt-container",
    onAnimationEnd: onAnimationEnd,
    children: /*#__PURE__*/_jsx(ChatWidgetLocaleContextConsumer, {
      children: locale => /*#__PURE__*/_jsxs(ContentContainer, {
        "data-test-id": "gdpr-consent-prompt",
        style: {
          animation: `${state.isEntering ? 'consent-modal-content-fadeIn' : 'consent-modal-content-fadeOut'} .3s`
        },
        children: [headerMessage && /*#__PURE__*/_jsx(Header, {
          children: headerMessage
        }), /*#__PURE__*/_jsx(SmallNoParagraphPadding, {
          use: "help",
          role: "document",
          tabIndex: 0,
          $useV2Style: useV2Style,
          "data-test-id": "gdpr-consent-text",
          dangerouslySetInnerHTML: {
            __html: formatHtml(message)
          }
        }), /*#__PURE__*/_jsx(ButtonsContainer, {
          children: useV2Style ? /*#__PURE__*/_jsxs(_Fragment, {
            children: [/*#__PURE__*/_jsx(AIConsentButton, {
              onClick: () => {
                onClose(onClick);
              },
              use: "primary",
              "data-test-id": "gdpr-consent-agree-button",
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "conversations-visitor-experience-components.buttons.agree",
                options: {
                  locale
                }
              })
            }), /*#__PURE__*/_jsx(AIConsentButton, {
              onClick: () => {
                onClose(onDeclineClick);
              },
              "data-test-id": "gdpr-consent-dismiss-button",
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "conversations-visitor-experience-components.buttons.disagree",
                options: {
                  locale
                }
              })
            })]
          }) : /*#__PURE__*/_jsxs(_Fragment, {
            children: [/*#__PURE__*/_jsx(LegacyFullWidthButton, {
              onClick: () => {
                onClose(onClick);
              },
              "data-test-id": "gdpr-consent-agree-button",
              use: "primary",
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "conversations-visitor-experience-components.buttons.agree",
                options: {
                  locale
                }
              })
            }), /*#__PURE__*/_jsx(LegacyFullWidthButton, {
              onClick: () => {
                onClose(onDeclineClick);
              },
              "data-test-id": "gdpr-consent-dismiss-button",
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "conversations-visitor-experience-components.buttons.disagree",
                options: {
                  locale
                }
              })
            })]
          })
        })]
      }, "consent-modal-content-container")
    })
  }, `${Math.random}-consent-modal-container`);
};
ConsentModal.displayName = 'ConsentModal';
export default ConsentModal;